import { EmptyState, NoDataIcon, PageHeader } from '@lmc/ui-kit'
import Actions from 'actions'
import { showToast } from 'common/utils'
import { Spinner } from 'components/generic'
import { NAMESPACE } from 'locale'
import { shiftPasswordsConstants } from 'locale/shiftPasswords'
import Operations from 'operations'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { HashLink } from 'react-router-hash-link'
import styled from 'styled-components'
import { ShiftPasswordsList } from './index'

// The enable/disable toggle lives on the Settings page (CM-3909) — link to the
// Access Control section that holds it.
const SETTINGS_ACCESS_CONTROL_HASH = '/settings/home-settings#access-control'

export const ShiftPasswordsScreen = () => {
  const { t } = useTranslation(NAMESPACE.SHIFT_PASSWORDS)

  const dispatch = useDispatch()

  const home = useSelector((state) => state.global.home)

  const shifts = useSelector((state) => state.data.shifts)

  const hasShiftsEnabled = Boolean(home?.features?.shifts?.enabled)

  const isLoading = shifts === undefined

  useEffect(() => {
    if (hasShiftsEnabled) {
      dispatch(Operations.loadList('shifts'))
    }
  }, [dispatch, hasShiftsEnabled])

  const handleAdd = () => {
    const id = 'addShift'
    dispatch(Actions.openModal({ modalId: id }))
    dispatch(Actions.addForm({ formId: id }))
  }

  const handleDelete = async (id) => {
    // deleteShift swallows failures and resolves undefined, so only toast
    // success once it actually deleted; otherwise surface an error.
    const deleted = await dispatch(Operations.deleteShift(id))
    showToast(
      deleted
        ? {
            type: 'success',
            title: t(shiftPasswordsConstants.PASSWORD_DELETED)
          }
        : {
            type: 'error',
            title: t(shiftPasswordsConstants.PASSWORD_DELETE_FAILED)
          }
    )
  }

  return (
    <Wrapper data-testid="shift-passwords-screen">
      <PageHeader
        title={t(shiftPasswordsConstants.SHIFT_PASSWORDS)}
        subtitle={t(shiftPasswordsConstants.SHIFT_PASSWORDS_SUBTITLE)}
        testId="shift-passwords-header"
      />
      {!hasShiftsEnabled && (
        <EmptyState
          title={t(shiftPasswordsConstants.SHIFT_PASSWORDS_DISABLED_TITLE)}
          description={
            <span data-testid="shift-passwords-disabled-description">
              {t(shiftPasswordsConstants.SHIFT_PASSWORDS_DISABLED_DESC)}{' '}
              <SettingsLink to={SETTINGS_ACCESS_CONTROL_HASH} smooth>
                {t(shiftPasswordsConstants.GO_TO_SETTINGS)}
              </SettingsLink>
            </span>
          }
          icon={<NoDataIcon />}
          testId="shift-passwords-disabled"
        />
      )}
      {hasShiftsEnabled && isLoading && <Spinner />}
      {hasShiftsEnabled && !isLoading && (
        <ContentWrapper data-testid="shift-passwords-content">
          <ShiftPasswordsList
            onAdd={handleAdd}
            onDelete={handleDelete}
            shifts={shifts}
            testId="shift-passwords-list"
          />
        </ContentWrapper>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  margin: 0 auto;
  padding: 24px;
  width: 100%;
  max-width: 1080px;
`

const ContentWrapper = styled.div`
  margin-top: 24px;
`

const SettingsLink = styled(HashLink)`
  color: ${({ theme }) => theme.color.status.primary};
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.color.hover.primary};
  }
`

export default ShiftPasswordsScreen
